import React, { ComponentProps } from 'react';
import styled from 'styled-components';
import { Box } from '../layout/Box';
import { Stack } from '../layout/Stack';

const Indicator = styled.div`
    &[data-variant='story'] {
        flex-grow: 1;
        opacity: 0.4;

        &:after {
            content: '';
            display: block;
            height: 2px;
            border-radius: 2px;
            width: 100%;
            background: #ffffff;
        }

        &[data-modifier='active'] {
            opacity: 1;
        }
    }

    &[data-variant='dots'] {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 0;
        height: 0;
        will-change: transform;
        transform: scale(0) translateZ(0);
        transform-origin: center center;
        transition:
            transform 0.2s linear,
            width 0.2s linear,
            height 0.2s linear;

        &::after {
            content: '';
            display: block;
            width: 8px;
            height: 8px;
            border-radius: 50%;
            opacity: 0.25;
            background-color: var(--color-primary);
            transition: background-color 200ms cubic-bezier(0.25, 0.1, 0.25, 1);
        }

        &[data-modifier='near'],
        &[data-modifier='active'],
        &[data-modifier='far'] {
            width: 13px;
            height: 13px;
            box-sizing: border-box;
        }

        &[data-modifier='far'] {
            pointer-events: none;
            transform: scale(0.5) translateZ(0);
        }

        &[data-modifier='near'],
        &[data-modifier='active'] {
            transform: scale(1) translateZ(0);
        }

        &[data-modifier='active']::after {
            opacity: 1;
        }
    }
`;

const getModifier = (index: number, active: number) => {
    if (index === active) {
        return 'active';
    }

    if (Math.abs(index - active) <= 1) {
        return 'near';
    }

    if (Math.abs(index - active) <= 2) {
        return 'far';
    }

    return undefined;
};

interface CarouselIndicatorsProps extends ComponentProps<typeof Box> {
    variant?: 'dots' | 'story';
    activeIndex: number;
    count: number;
}

export const CarouselIndicators = (props: CarouselIndicatorsProps) => {
    const { variant = 'dots', activeIndex, count } = props;

    if (count <= 1) {
        return null;
    }

    return (
        <Stack
            count={count}
            direction="row"
            spacing={variant === 'story' ? 0.5 : 0}
            renderItem={(index) => (
                <Indicator
                    key={index}
                    data-variant={variant}
                    data-modifier={getModifier(index, activeIndex)}
                />
            )}
        />
    );
};
